import {
  Component,
  ComponentRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  TemplateRef,
  Type,
  ViewContainerRef,
  inject,
  input,
  viewChild,
} from '@angular/core';

import { IHeaderAngularComp } from 'ag-grid-angular';
import { Column, GridApi } from 'ag-grid-community';

import { CamDefaultPanelComponent, CamOverlayRef, OverlayService } from '@camelot/ui';

import { buildTableHeaderParams, isAngularComponent } from '../../../helpers/table-params';
import { CustomHeaderComponent } from '../../custom-header/custom-header.component';
import { GridTableColumnMenuComponent } from '../column-menu/grid-table-column-menu.component';

/**
 * En-tête de colonne de la vue tableau (`cam-grid-table`).
 *
 * Instancie le `headerComponent` de la colonne (celui de `fieldsOverrides`, sinon le
 * `agColumnHeader` enregistré sur la grille, sinon `cam-custom-header`) avec des `IHeaderParams`
 * équivalents à ceux d'AG Grid : le tri passe par `api.applyColumnState`, le filtre ouvre le menu
 * de colonne de la table (`cam-grid-table-column-menu` : tri + filtre) ancré sous cet en-tête.
 */
@Component({
  host: { class: 'grid-table-header-cell' },
  imports: [GridTableColumnMenuComponent],
  selector: 'cam-grid-table-header-cell',
  template: `
    <ng-container #outlet></ng-container>
    <ng-template #menu>
      <cam-grid-table-column-menu
        [api]="this.api()"
        [column]="this.column()"
        (closeEvent)="this.closeMenu()"
      ></cam-grid-table-column-menu>
    </ng-template>
  `,
})
export class GridTableHeaderCellComponent<T> implements OnChanges, OnDestroy {
  api = input.required<GridApi<T>>();
  column = input.required<Column>();
  /** Incrémenté par la table à chaque changement de tri / filtre : force le re-rendu de l'en-tête. */
  version = input(0);

  private readonly _outlet = viewChild.required('outlet', { read: ViewContainerRef });
  private readonly _menuTemplate = viewChild.required('menu', { read: TemplateRef });
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _overlayService = inject(OverlayService);

  private _componentRef: ComponentRef<IHeaderAngularComp> | null = null;
  private _menuRef: CamOverlayRef | null = null;

  ngOnChanges(): void {
    this._render();
  }

  ngOnDestroy(): void {
    this.closeMenu();
    this._clear();
  }

  public openMenu(): void {
    this.closeMenu();
    this._menuRef = this._overlayService.openMenu({
      matchTriggerWidth: false,
      menuComponent: CamDefaultPanelComponent,
      onClose: () => (this._menuRef = null),
      shadow: 'brand',
      template: this._menuTemplate(),
      triggerElement: this._host.nativeElement,
    });
  }

  public closeMenu(): void {
    if (!this._menuRef) {
      return;
    }
    this._overlayService.closeMenu(this._menuRef);
    this._menuRef = null;
  }

  private _render(): void {
    this._clear();

    const api = this.api();
    const column = this.column();
    const params = buildTableHeaderParams(api, column, this._host.nativeElement, () => this.openMenu());

    this._componentRef = this._outlet().createComponent(this._resolveHeaderComponent(api, column));
    this._componentRef.instance.agInit(params);
  }

  private _resolveHeaderComponent(api: GridApi<T>, column: Column): Type<IHeaderAngularComp> {
    const components = api.getGridOption('components') ?? {};
    const headerComponent = column.getColDef().headerComponent;
    const candidate = typeof headerComponent === 'string' ? components[headerComponent] : headerComponent;

    if (isAngularComponent(candidate)) {
      return candidate as Type<IHeaderAngularComp>;
    }
    const registered = components['agColumnHeader'];
    return isAngularComponent(registered) ? (registered as Type<IHeaderAngularComp>) : CustomHeaderComponent;
  }

  private _clear(): void {
    this._componentRef?.destroy();
    this._componentRef = null;
  }
}
